/**
 * @fileoverview Send-invitation mutation hook.
 * @description Wraps POST /identity/invitations in a TanStack mutation and refreshes the
 * Invitations list once the invitation is created.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { USER_DIRECTORY_QUERY_KEY } from '@/features/user-invitations/hooks/useUserInvitationActions'
import { sendInvitation } from '@/features/user-invitations/services/user-invitations.service'
import { INVITATIONS_QUERY_KEY } from '@/shared/constants/invitations-query-keys.constants'

import type {
  SendInvitationRequest,
  SendInvitationResponse,
} from '@/features/user-invitations/types/user-invitations.types'
import type { ApiError } from '@/shared/types/http.types'

/**
 * @description Sends an invitation.
 * @returns {ReturnType<typeof useMutation<SendInvitationResponse, ApiError,
 * SendInvitationRequest>>} The mutation object (mutate, isPending, error, ...).
 */
export function useSendInvitation() {
  const queryClient = useQueryClient()

  return useMutation<SendInvitationResponse, ApiError, SendInvitationRequest>({
    mutationFn: sendInvitation,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: INVITATIONS_QUERY_KEY })
      // The user directory (GET /users) lists the invitee, so refresh it too.
      void queryClient.invalidateQueries({ queryKey: USER_DIRECTORY_QUERY_KEY })
    },
  })
}
